<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['confirm']);
const { t } = useI18n();
const dialogRef = ref(null);

// Options must mirror SynkraSubscription::CANCELLATION_REASONS on the
// backend - a mismatch means the model's inclusion validation rejects
// the cancel request and the customer can't cancel at all.
const REASONS = [
  'too_expensive',
  'not_using',
  'missing_feature',
  'switching',
  'other',
];

const reason = ref('');
const feedback = ref('');

const resetForm = () => {
  reason.value = '';
  feedback.value = '';
};

const handleConfirm = () => {
  if (!reason.value) {
    // Dialog is deliberately still open here - a customer clicking
    // Confirm without picking a reason gets a nudge, not a silent
    // closing of the dialog followed by an "unknown error" toast from
    // the backend validation.
    useAlert(t('SYNKRA_BILLING_SETTINGS.CANCEL_DIALOG.REASON_REQUIRED'));
    return;
  }
  const payload = {
    reason: reason.value,
    feedback: feedback.value.trim() || null,
  };
  // Reset immediately so the form is clean the next time the dialog
  // opens (this component stays mounted across opens).
  resetForm();
  emit('confirm', payload);
};

defineExpose({ dialogRef, resetForm });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    :title="t('SYNKRA_BILLING_SETTINGS.CANCEL_DIALOG.TITLE')"
    :description="t('SYNKRA_BILLING_SETTINGS.CANCEL_DIALOG.DESCRIPTION')"
    :confirm-button-label="t('SYNKRA_BILLING_SETTINGS.CANCEL_DIALOG.CONFIRM')"
    :cancel-button-label="t('SYNKRA_BILLING_SETTINGS.CANCEL_DIALOG.DISMISS')"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-3 text-left">
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('SYNKRA_BILLING_SETTINGS.CANCEL_DIALOG.REASON_LABEL') }}
      </p>
      <div class="flex flex-col gap-2">
        <label
          v-for="option in REASONS"
          :key="option"
          class="flex items-center gap-2 cursor-pointer text-sm text-n-slate-12"
        >
          <input
            v-model="reason"
            type="radio"
            :value="option"
            name="cancel_reason"
            class="h-4 w-4 cursor-pointer"
          />
          <span>{{
            t(`SYNKRA_BILLING_SETTINGS.CANCEL_DIALOG.REASONS.${option.toUpperCase()}`)
          }}</span>
        </label>
      </div>
      <div class="flex flex-col gap-1 mt-2">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('SYNKRA_BILLING_SETTINGS.CANCEL_DIALOG.FEEDBACK_LABEL') }}
        </p>
        <textarea
          v-model="feedback"
          rows="3"
          maxlength="2000"
          class="w-full rounded border border-n-weak px-3 py-2 text-sm text-n-slate-12 bg-n-alpha-1"
          :placeholder="
            t('SYNKRA_BILLING_SETTINGS.CANCEL_DIALOG.FEEDBACK_PLACEHOLDER')
          "
        />
      </div>
    </div>
  </Dialog>
</template>
